import { create } from 'zustand'
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware'
import { applyMaterialModes } from '../constants/materialConfig'

const useEditorStore = create(
  persist(
    devtools(
      subscribeWithSelector((set, get) => ({
      // ===== SCENE MANAGEMENT =====
      sceneRefs: {
        scene: null,
        camera: null,
        renderer: null,
        controls: null,
        shadedMesh: null,
        material: null,
        unmodifiedMesh: null,
        originalCamera:null,
        normalMaterial: null,
      },  /* scene, camera, renderer, controls, shadedMesh, unmodifiedMesh*/
      setSceneRefs: (refs) => set({ sceneRefs: refs }),

      // Geometry version tracking for cache invalidation across tools
      // Incremented when geometry is modified in-place (e.g., applyMatrix4, vertex operations)
      geometryVersion: 0,
      incrementGeometryVersion: () => {
        const newVersion = get().geometryVersion + 1;
        console.log('🔄 Geometry version incremented:', newVersion);
        set({ geometryVersion: newVersion });
      },

      replaceShadedMesh: (newMesh) => {
        const { sceneRefs, setSceneRefs, incrementGeometryVersion } = useEditorStore.getState();

        if (!sceneRefs || !sceneRefs.scene || !sceneRefs.shadedMesh) {
            console.error("Cannot replace mesh, scene references are missing.");
            return;
        }

        const scene = sceneRefs.scene;
        const oldMesh = sceneRefs.shadedMesh;

        // console.log('🔄 EditorStore: Replacing shaded mesh:', oldMesh.name, 'with:', newMesh.name);

        // 1. Remove the old mesh from the scene graph
        scene.remove(oldMesh);
        // It's also good practice to dispose of old geometry to free GPU memory
        oldMesh.geometry.dispose();

        // 2. Add the new mesh to the scene graph
        scene.add(newMesh);

        // 3. Update the state to point to the new mesh
        setSceneRefs({ ...sceneRefs, shadedMesh: newMesh });

        // 4. Increment geometry version to invalidate caches in tools (e.g., FlaresHandler)
        incrementGeometryVersion();
      },

      replaceCameraAndControls: (newCamera, newControls) => {
        const {sceneRefs, setSceneRefs} = useEditorStore.getState();
        if (!sceneRefs || !sceneRefs.camera || !sceneRefs.controls) {
          console.error("Cannot replace camera and controls, scene references are missing.");
          return;
        }
        setSceneRefs({ ...sceneRefs, camera: newCamera, controls: newControls})
      },

      showUnmodifiedMesh: false,
      toggleUnmodifiedMesh: () => {
        const um = get().sceneRefs?.unmodifiedMesh;
        if(um)
        {
          const toggle = get().showUnmodifiedMesh
          um.visible = !toggle;
          set({showUnmodifiedMesh: !toggle});
        }
      },

      ghostModeEnabled: false,
      toggleGhostMode: () => {
        const { sceneRefs } = get();
        const material = sceneRefs?.material;

        if (!material) return;

        const newGhostState = !get().ghostModeEnabled;
        const colorModeState = get().colorModeEnabled;

        applyMaterialModes(material, newGhostState, colorModeState);
        set({ ghostModeEnabled: newGhostState });
      },

      colorModeEnabled: false,
      toggleColorMode: () => {
        const { sceneRefs } = get();
        const material = sceneRefs?.material;

        if (!material) return;

        const ghostModeState = get().ghostModeEnabled;
        const newColorState = !get().colorModeEnabled;

        applyMaterialModes(material, ghostModeState, newColorState);
        set({ colorModeEnabled: newColorState });
      },

      normalModeEnabled: false,
      toggleNormalMode: () => {
        const { sceneRefs } = get();
        const shadedMesh = sceneRefs?.shadedMesh;
        const normalMaterial = sceneRefs?.normalMaterial;
        const originalMaterial = sceneRefs?.material;

        if (!shadedMesh || !normalMaterial || !originalMaterial) return;

        const newNormalState = !get().normalModeEnabled;
        shadedMesh.material = newNormalState ? normalMaterial : originalMaterial;
        set({ normalModeEnabled: newNormalState });
      },

      interactiveObjects: [],
      setInteractiveObjects: (objects) => set({ interactiveObjects: objects }),

      cursorOverride: null, // Can be 'none', 'crosshair', etc.
      setCursorOverride: (style) => set({ cursorOverride: style }),

      // Painting cursor UI (circle overlay)
      paintingCursor: { enabled: false, radiusPx: 10, color: '#ffffff' },
      setPaintingCursor: (data) => set(state => ({ paintingCursor: { ...state.paintingCursor, ...data } })),

      // ===== TOOL MANAGEMENT =====
      activeTool: 'none',
      setActiveTool: (tool) => set({ activeTool: tool }),

      // Tool settings for each tool type
      toolSettings: {
        // Scan Process - Preparing Scan
        scan_preparing_scissors: {
          enabled: true
        },
        scan_preparing_eraser: {
          enabled: true
        },
        scan_preparing_pencil: {
          enabled: true
        },
        
        // Scan Process - Axes Alignment
        scan_axes_pin: {
          enabled: true
        },
        scan_axes_movement: {
          showLasers: true,
          currentView: 'face'
        },
        
        // Main Process - Rectification
        main_rectification_measure_reduction: {
          enabled: true
        },
        main_rectification_scoop_recharge: {
          enabled: true
        },
        main_rectification_blocking_zone: {
          enabled: true
        },
        main_rectification_global_reduction: {
          enabled: true
        },
        
        // Main Process - Elongation
        main_elongation_elongation: {
          enabled: false,
          value: 0
        },
        
        // Main Process - Trimline
        main_trimline_cut: {
          thickness: 0.1,
          smooth: true
        },
        main_trimline_line: {
          enabled: true
        },
        main_trimline_measure: {
          unit: 'mm',
          precision: 2
        },
        
        // Main Process - Flares
        main_flares_flare: {
          enabled: true
        },
        main_flares_smoothning: {
          enabled: true
        },
        main_flares_adjust: {
          enabled: true
        },
        main_flares_preview: {
          enabled: true
        },
        
        // Main Process - Thickness
        main_thickness_thickness: {
          value: 3.0,
          unit: 'mm'
        },
        main_thickness_measure: {
          unit: 'mm',
          precision: 2
        },
        main_thickness_adjust: {
          enabled: true
        },
        
        // Main Process - Locking Mechanism (removed - now managed by dedicated store)
        main_locking_position: {
          enabled: true
        },
        main_locking_adjust: {
          enabled: true
        },
        
        // Main Process - Alignment
        
        // Main Process - Valve
        main_valve_valve: {
          enabled: true
        },
        main_valve_position: {
          enabled: true
        },
        main_valve_adjust: {
          enabled: true
        },
        
        // Main Process - Pads
        main_pads_pad: {
          enabled: true
        },
        main_pads_position: {
          enabled: true
        },
        main_pads_adjust: {
          enabled: true
        },
        
        // Main Process - Finalization
        main_finalization_review: {
          enabled: true
        },
        main_finalization_export: {
          enabled: true
        },
        main_finalization_save: {
          enabled: true
        }
      },
      setToolSetting: (tool, setting, value) => {
        set(state => ({
          toolSettings: {
            ...state.toolSettings,
            [tool]: {
              ...state.toolSettings[tool],
              [setting]: value
            }
          }
        }));
      },

      // ===== 3D MODEL STATE =====
      modelLoaded: false,
      setModelLoaded: (loaded) => set({ modelLoaded: loaded }),

      // Model path selection for debug/testing
      selectedModelPath: '/models/AK020180_Scan (2).ply',
      setSelectedModelPath: (path) => set({ selectedModelPath: path }),

      // ===== UPLOADED MODEL STATE =====
      uploadedModelData: null, // Stores uploaded model file data and Blob URLs
      setUploadedModelData: (data) => set({ uploadedModelData: data }),

      // Model scaling state
      modelScaleInfo: null, // Stores scaling information from ModelScaler
      setModelScaleInfo: (info) => set({ modelScaleInfo: info }),

      modelScaleUnit: 'auto', // User override: 'auto', 'mm', 'cm', 'm', 'inches'
      setModelScaleUnit: (unit) => set({ modelScaleUnit: unit }),

      modelManualScale: 1.0, // Additional manual scale adjustment
      setModelManualScale: (scale) => set({ modelManualScale: scale }),

      cameraPosition: null,
      setCameraPosition: (cameraPosition) => set({cameraPosition: cameraPosition}),

      setCamera: (newCamera) => {
        const { sceneRefs } = get(); // Use get() to access current state

        // On the first switch, save the original camera so we can revert later
        if (!sceneRefs.originalCamera) {
            sceneRefs.originalCamera = sceneRefs.camera;
        }

        // Update OrbitControls to use the new camera
        if (sceneRefs.controls) {
            sceneRefs.controls.object = newCamera;
            sceneRefs.controls.update(); // Force an update
        }
        
        // Update the state to trigger re-renders in components
        set({
            sceneRefs: { ...sceneRefs, camera: newCamera }
        });
      },
        
      // Action to restore the original camera
      restoreOriginalCamera: () => {
          const { sceneRefs } = get();
          if (sceneRefs.originalCamera) {
              setCamera(sceneRefs.originalCamera);
              // Clear originalCamera so it can be set again next time
              set(state => ({
                  sceneRefs: { ...state.sceneRefs, originalCamera: null }
              }));
          }
      },
      // Model transformations
      modelTransform: {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 }
      },
      setModelTransform: (transform) => set({ modelTransform: transform }),

      // ===== MEASUREMENT TOOL =====
      measuringMode: false,
      setMeasuringMode: (mode) => set({ measuringMode: mode }),

      points: [],
      setPoints: (points) => set({ points }),

      distance: null,
      setDistance: (distance) => set({ distance }),

      pointMeshes: [],
      setPointMeshes: (meshes) => set({ pointMeshes: meshes }),

      // ===== WIREFRAME TOGGLE =====
      wireframeVisible: false,
      setWireframeVisible: (visible) => set({ wireframeVisible: visible }),
      toggleWireframe: () => {
        const sm = get().sceneRefs?.shadedMesh;
        if(sm)
        {
          const toggle = get().wireframeVisible;
          sm.material.wireframe = !toggle;
          set({wireframeVisible: !toggle});
        }
      },

      // ===== UNDO/REDO SYSTEM =====
      history: [],
      historyIndex: -1,

      execCommand: (command) => {
        command.execute();
        set(state => {
          const nextIndex = state.historyIndex + 1;
          const newHistory = state.history.slice(0, nextIndex);
          newHistory.push(command);
          return {
            history: newHistory,
            historyIndex: nextIndex
          };
        });
      },

      addToHistory: (action) => {
        set(state => {
          const newHistory = state.history.slice(0, state.historyIndex + 1);
          newHistory.push(action);
          return {
            history: newHistory,
            historyIndex: newHistory.length - 1
          };
        });
      },

      undo: () => {
        set(state => {
            if (state.historyIndex < 0) return state; // Nothing to undo

            const commandToUndo = state.history[state.historyIndex];
            if (commandToUndo.undo) {
                commandToUndo.undo(); // <-- This will call our trimCommand.undo()
            }

            return { historyIndex: state.historyIndex - 1 };
        });
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextIndex = historyIndex + 1;
          history[nextIndex].execute();
          set({ historyIndex: nextIndex });
        }
      },

      // Reset history up to a specific command
      resetHistoryUpTo: (targetCommand) => {
        set(state => {
          const { history, historyIndex } = state;
          
          // Find the index of the target command in history
          const targetIndex = history.findIndex(cmd => cmd === targetCommand);
          
          if (targetIndex === -1) {
            console.warn('Target command not found in history');
            return state;
          }
          
          // Keep history up to and including the target command
          const newHistory = history.slice(0, targetIndex + 1);
          const newHistoryIndex = targetIndex;
          
          return {
            history: newHistory,
            historyIndex: newHistoryIndex
          };
        });
      },

      // ===== STEP-SPECIFIC STATE =====
      // Store step-specific data
      stepData: {},
      setStepData: (step, data) => {
        set(state => ({
          stepData: {
            ...state.stepData,
            [step]: data
          }
        }));
      },

      getStepData: (step) => {
        const { stepData } = get();
        return stepData[step] || {};
      },

      // ===== UI STATE =====
      // Right panel visibility
      rightPanelVisible: true,
      setRightPanelVisible: (visible) => set({ rightPanelVisible: visible }),

      // Left toolbar visibility
      leftToolbarVisible: true,
      setLeftToolbarVisible: (visible) => set({ leftToolbarVisible: visible }),

      // Loading states
      isLoading: false,
      loadingMessage: null,
      setIsLoading: (loading, message = null) => set({ isLoading: loading, loadingMessage: loading ? message : null }),
      setLoadingMessage: (message) => set({ loadingMessage: message }),

      // Worker-specific loading states
      isWorkerBusy: false,
      workerProgress: null, // { operation: string, progress: number (0-100) }
      setWorkerLoading: (busy, operation = null, progress = null) => {
        console.log('[EditorStore] setWorkerLoading called with:', { busy, operation, progress });
        set({
          isWorkerBusy: busy,
          workerProgress: busy ? { operation, progress } : null
        });
        console.log('[EditorStore] State updated. New state:', { isWorkerBusy: busy, workerProgress: busy ? { operation, progress } : null });
      },

      // Error state
      error: null,
      setError: (error) => set({ error }),

      // ===== UTILITY FUNCTIONS =====
      resetScene: () => {
        set({
          activeTool: 'none',
          measuringMode: false,
          paintingMode: false,
          points: [],
          distance: null,
          wireframeVisible: false,
          modelTransform: {
            position: { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 }
          }
        });
      },

      // Clear all tool states
      clearToolStates: () => {
        set({
          activeTool: 'none',
          measuringMode: false,
          paintingMode: false,
          points: [],
          distance: null
        });
      }
    }))
    ),
    {
      name: 'editor-store',
      partialize: (state) => ({
        selectedModelPath: state.selectedModelPath,
        modelScaleUnit: state.modelScaleUnit,
        modelManualScale: state.modelManualScale,
      }),
    }
  )
);

export default useEditorStore;
